import React, { useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SettingsBackupRestoreOutlinedIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  DataGrid,
  GridActionsCellItem,
  GridRowModes,
  GridToolbar,
} from '@mui/x-data-grid';

import './CustomEditTable.scss';
import { copyDeep } from '../../../shared/utility';

const EditedMarkColumn = props => {
  const { edited } = props.row;
  if (edited) {
    //  return <EditIcon />;
    return <div className="edited-icon"></div>;
  }
  return <></>;
};

const EDIT_MARK_COLUMN = {
  field: 'editedMark',
  headerName: '',
  type: 'custom',
  resizable: false,
  filterable: false,
  sortable: false,
  editable: false,
  groupable: false,
  display: 'flex',
  flex: 0.2,
  minWidth: 50,
  resizable: false,

  renderCell: params => <EditedMarkColumn {...params} />,
};

const CustomEditTable = props => {
  const {
    rows,
    columns,
    editColumnName,
    defaultValueColumnName,
    onEditedRowsChange,
  } = props;

  const [rowsData, setRowsData] = useState([]);

  const [rowModesModel, setRowModesModel] = useState({});

  const [pinnedRowsIds, setPinnedRowsIds] = useState([]);

  useEffect(() => {
    setRowsData(rows);
    let pinned = [];
    rows.forEach(row => {
      if (row[editColumnName] != row[defaultValueColumnName]) {
        pinned.push(row.id);
      }
    });
    setPinnedRowsIds(pinned);
  }, [rows]);

  const { unpinnedRows, pinnedRows } = React.useMemo(() => {
    const unpinnedRows = [];
    const pinnedRowsData = [];

    rowsData.forEach(row => {
      if (pinnedRowsIds.includes(row.id)) {
        pinnedRowsData.push({ ...row, edited: true });
      } else {
        unpinnedRows.push({ ...row, edited: false });
      }
    });

    onEditedRowsChange(pinnedRowsData);

    return {
      unpinnedRows: unpinnedRows,
      pinnedRows: pinnedRowsData,
    };
  }, [pinnedRowsIds, rowsData]);

  const getActions = ({ id }) => {
    const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

    if (isInEditMode) {
      return [
        <GridActionsCellItem
          icon={<SaveIcon />}
          label="Save"
          sx={{
            color: 'primary.main',
          }}
          onClick={handleSaveClick(id)}
        />,
        <GridActionsCellItem
          icon={<CancelIcon />}
          label="Cancel"
          className="textPrimary"
          onClick={handleCancelClick(id)}
          color="inherit"
        />,
      ];
    }

    return [
      <GridActionsCellItem
        icon={<EditIcon />}
        label="Edit"
        className="textPrimary"
        onClick={handleEditClick(id)}
        color="inherit"
      />,
      <GridActionsCellItem
        icon={<SettingsBackupRestoreOutlinedIcon />}
        label="Restore to Default"
        onClick={handleRestoreDefaultClick(id)}
        color="inherit"
      />,
    ];
  };

  const ACTIONS_COLUMN = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    flex: 0.5,
    minWidth: 150,
    resizable: false,
    cellClassName: 'actions',
    getActions: getActions,
  };

  const getRows = () => [...pinnedRows, ...unpinnedRows];

  const getColumns = () => [EDIT_MARK_COLUMN, ...columns, ACTIONS_COLUMN];

  const handleEditClick = id => () => {
    setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  };

  const handleSaveClick = id => () => {
    setRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View },
    }));
  };

  const restoreDefaultRowValue = (id, rows) => {
    const newRows = rows.map(row => {
      if (row.id == id) {
        const newRow = copyDeep(row);
        newRow[editColumnName] = newRow[defaultValueColumnName];
        newRow['edited'] = false;
        return newRow;
      }
      return row;
    });
    return newRows;
  };

  const handleRestoreDefaultClick = id => () => {
    setRowsData(prevRows => restoreDefaultRowValue(id, prevRows));
    setPinnedRowsIds(pinnedRowsIds.filter(pinnedId => pinnedId !== id));
    setRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  const handleCancelClick = id => () => {
    setRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  const processRowUpdate = (updatedRow, originalRow) => {
    let newRow = updatedRow;
    if (updatedRow[editColumnName] != originalRow[editColumnName]) {
      newRow = { ...updatedRow, edited: true };
      setRowsData([
        ...rowsData.filter(row => row.id !== updatedRow.id),
        newRow,
      ]);
      if (!pinnedRowsIds.includes(updatedRow.id)) {
        setPinnedRowsIds(prev => [...prev, updatedRow.id]);
      }
    }
    return newRow;
  };

  return (
    <DataGrid
      disableColumnSorting
      disableColumnFilter
      disableColumnSelector
      disableDensitySelector
      disableColumnMenu
      rows={getRows()}
      columns={getColumns()}
      rowModesModel={rowModesModel}
      processRowUpdate={processRowUpdate}
      slots={{ toolbar: GridToolbar }}
      slotProps={{
        toolbar: {
          showQuickFilter: true,
          printOptions: { disableToolbarButton: true },
          csvOptions: { disableToolbarButton: true },
        },
      }}
    />
  );
};

export default CustomEditTable;
